import { Injectable } from "@angular/core";
import {
  TextElement,
  ImageElement,
  ShapeElement,
  TableElement,
  TableCellStyle,
  Position,
  Size,
  ShapeType,
} from "../types/canvas.types";

@Injectable({
  providedIn: "root",
})
export class ElementFactoryService {
  createTextElement(
    position: Position,
    content: string = "Your text here"
  ): TextElement {
    return {
      id: this.generateId(),
      type: "text",
      position,
      size: { width: 200, height: 50 },
      zIndex: 1,
      content,
      fontSize: 16,
      fontFamily: "Inter",
      fontWeight: "400",
      color: "#1f2937",
      textAlign: "left",
      bold: false,
      italic: false,
      underline: false,
    };
  }

  createImageElement(
    position: Position,
    url: string,
    size?: Size
  ): ImageElement {
    return {
      id: this.generateId(),
      type: "image",
      position,
      size: size || { width: 200, height: 150 },
      zIndex: 1,
      url,
      alt: "Canvas image",
    };
  }

  createShapeElement(
    position: Position,
    shapeType: ShapeType,
    size?: Size
  ): ShapeElement {
    const defaultSize =
      size ||
      (shapeType === "line" ||
      shapeType === "arrow" ||
      shapeType === "double-arrow"
        ? { width: 160, height: 25 }
        : { width: 100, height: 100 });

    const element: ShapeElement = {
      id: this.generateId(),
      type: "shape",
      position,
      size: defaultSize,
      zIndex: 1,
      shapeType,
      fill: "transparent", // All shapes now start with transparent fill
      stroke: "#1d4ed8",
      strokeWidth: 2,
    };

    if (
      shapeType === "line" ||
      shapeType === "arrow" ||
      shapeType === "double-arrow"
    ) {
      const midpointY = defaultSize.height / 2;
      element.linePoints = {
        start: { x: 0, y: midpointY },
        end: { x: defaultSize.width, y: midpointY },
      };
    }

    return element;
  }

  createTableElement(
    position: Position,
    rows: number,
    columns: number,
    size?: Size
  ): TableElement {
    const sanitizedRows = Math.max(1, Math.min(rows, 20));
    const sanitizedColumns = Math.max(1, Math.min(columns, 12));
    const cellData = Array.from({ length: sanitizedRows }, () =>
      Array.from({ length: sanitizedColumns }, () => "")
    );
    const defaultCellStyle: TableCellStyle = {
      fontSize: 14,
      fontFamily: "Inter",
      color: "#1f2937",
      textAlign: "left",
      bold: false,
      italic: false,
      underline: false,
    };
    const cellStyles = Array.from({ length: sanitizedRows }, () =>
      Array.from({ length: sanitizedColumns }, () => ({ ...defaultCellStyle }))
    );

    return {
      id: this.generateId(),
      type: "table",
      position,
      size: size || { width: 320, height: 180 },
      zIndex: 1,
      rows: sanitizedRows,
      columns: sanitizedColumns,
      cellData,
      cellStyles,
      headerRows: 1,
      borderColor: "#1d4ed8",
      cellPadding: 8,
      backgroundColor: "rgba(255,255,255,0.9)",
      headerBackgroundColor: "rgba(37,99,235,0.08)",
      textColor: "#1f2937",
    };
  }

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }
}
